import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import projects from "../data/projects";

export default function PortfolioCarousel() {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: false,
    dragFree: true,
    containScroll: "trimSnaps",
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
    setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  const prev = () => emblaApi && emblaApi.scrollPrev();
  const next = () => emblaApi && emblaApi.scrollNext();

  return (
    <section
      id="work"
      data-testid="portfolio-section"
      className="relative py-24 md:py-40 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-12 md:mb-16">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#f5e6d3]" />
              <span className="font-display text-xs uppercase tracking-[0.3em] text-[#f5e6d3]">
                Selected Work — 004
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] tracking-tight">
              Recent
              <br />
              <span className="font-editorial italic font-light normal-case lowercase text-[#f5e6d3]">
                projects.
              </span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="carousel-prev"
              onClick={prev}
              disabled={!canPrev}
              aria-label="Previous project"
              className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-white"
            >
              <ArrowLeft size={18} />
            </button>
            <button
              data-testid="carousel-next"
              onClick={next}
              disabled={!canNext}
              aria-label="Next project"
              className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-white"
            >
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="embla pl-6 md:pl-12" ref={emblaRef}>
        <div className="embla__container gap-6 md:gap-10 pr-6 md:pr-12">
          {projects.map((p, idx) => (
            <Link
              key={p.id}
              to={`/project/${p.id}`}
              data-testid={`carousel-slide-${p.id}`}
              className="embla__slide group w-[85vw] sm:w-[60vw] md:w-[44vw] lg:w-[38vw]"
            >
              <div className="relative overflow-hidden aspect-[4/5] bg-zinc-900">
                <img
                  src={p.cover}
                  alt={p.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                <div className="absolute top-4 left-4 font-display text-xs uppercase tracking-[0.25em] text-[#f5e6d3]">
                  {p.index}
                </div>
                <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center group-hover:bg-[#f5e6d3] group-hover:text-black transition-colors">
                  <ArrowUpRight size={16} />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
                  <div className="font-display text-xs uppercase tracking-[0.2em] text-zinc-300 mb-2">
                    {p.category} · {p.year}
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight leading-tight">
                    {p.title}
                  </h3>
                </div>
              </div>
              <p className="mt-4 font-editorial italic text-base md:text-lg text-zinc-400 leading-snug max-w-md">
                {p.short}
              </p>
            </Link>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-10 flex items-center gap-2">
        {projects.map((_, i) => (
          <span
            key={i}
            className={`h-px transition-all duration-500 ${
              i === selected ? "w-12 bg-[#f5e6d3]" : "w-6 bg-white/20"
            }`}
          />
        ))}
        <span className="ml-4 font-display text-xs uppercase tracking-[0.25em] text-zinc-500">
          {String(selected + 1).padStart(2, "0")} /{" "}
          {String(projects.length).padStart(2, "0")}
        </span>
      </div>
    </section>
  );
}
